import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AUTH } from "@/constants/testIds";
import { ShieldCheck, Globe } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: Desenzano del Garda lakefront */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src="https://customer-assets-39nsmqrw.emergentagent.net/job_attestguard/artifacts/cdx0zx75_image.png"
          alt="Desenzano del Garda"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(24,15%,10%)]/25 via-[hsl(24,15%,10%)]/40 to-[hsl(24,15%,10%)]/75" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-[hsl(60,20%,98%)]">
          <div className="inline-flex items-center bg-white rounded-lg px-4 py-2.5 shadow-md w-fit">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_attestguard/artifacts/3wuvsw34_FORMATO-WEB-NERO.jpg"
              alt="Sicurgarda"
              className="h-12 w-auto"
            />
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight drop-shadow-lg">
              Sicurezza,<br/>formazione,<br/>consulenza.
            </h1>
            <p className="mt-4 text-base opacity-90 max-w-md drop-shadow">
              {t("tagline")}
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
              <span className="w-8 h-px bg-white/60" />
              Desenzano del Garda
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <div className="lg:hidden">
              <img
                src="https://customer-assets-39nsmqrw.emergentagent.net/job_attestguard/artifacts/3wuvsw34_FORMATO-WEB-NERO.jpg"
                alt="Sicurgarda"
                className="h-9 w-auto"
              />
            </div>
            <button
              onClick={() => setLang(lang === "it" ? "en" : "it")}
              data-testid={AUTH.langSwitch}
              className="ml-auto inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground btn-lift"
            >
              <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
            </button>
          </div>

          <h2 className="text-3xl font-bold tracking-tight">{t("login")}</h2>
          <p className="text-muted-foreground text-sm mt-2">
            {lang === "it" ? "Accedi al tuo pannello Sicurgarda." : "Sign in to your Sicurgarda dashboard."}
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">{t("email")}</Label>
              <Input
                id="email" type="email" required autoFocus
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid={AUTH.loginEmail}
                className="mt-2 h-12"
                placeholder="admin@sicurgarda.it"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">{t("password")}</Label>
              <Input
                id="password" type="password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid={AUTH.loginPassword}
                className="mt-2 h-12"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid={AUTH.loginError} className="text-sm text-[hsl(0,70%,40%)] bg-[hsl(0,90%,94%)] border border-[hsl(0,70%,85%)] rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit" disabled={loading}
              data-testid={AUTH.loginSubmit}
              className="w-full h-12 rounded-full text-sm tracking-wide btn-lift"
            >
              {loading ? "…" : t("login_cta")}
            </Button>
          </form>

          <div className="mt-6 flex items-center justify-between text-xs">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-muted-foreground hover:text-primary btn-lift">
              {t("forgot_password")}
            </Link>
          </div>

          <div className="mt-10 text-xs text-muted-foreground">
            {lang === "it"
              ? "Sistema multi-tenant · Ruoli: Super Admin · Operatore · Cliente"
              : "Multi-tenant · Roles: Super Admin · Operator · Client"}
          </div>
        </div>
      </div>
    </div>
  );
}
